import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams, useHistory } from 'react-router-dom'
import { theme } from 'theme'
import { useNewTranslation } from 'hooks'
import styled from 'particles/themes/styled'
import { PredefinedModal, MODAL_TYPE, ModalState } from './Modals'
import { selectTeam } from 'state'
import { useCheckTeamPermissionFunction, usePermission } from 'hooks/permissions'
import IconButton from 'particles/Button/IconButton'
import Tab, { TabOption } from 'particles/Tab'
import { spacing } from 'particles/styles'
import Menu from 'particles/Menu'
import MenuItem from 'particles/MenuItem'
import { selectIsInternalUser } from 'state/profile'
import { CampaignStatusControl } from 'components/Shared/Permission/enums'

export enum SwitchViewType {
    MEMBERS = 'members',
    ROLE_MANAGEMENT = 'roleManagement',
    BRAND_ACCESS = 'brandAccess',
    BUSINESS_DETAILS = 'businessDetails',
}
interface HeaderProps {
    switchProps: {
        className?: string
        currentViewType: string
    }
    showTab?: boolean
    className?: string
    handleLeavePageWithoutSave: (type: SwitchViewType) => void
    isNotEdited: boolean
    hideTeamTooltip?: boolean
}
type TypeState = MODAL_TYPE | undefined

const Header: React.FC<HeaderProps> = ({
    className,
    switchProps,
    showTab = true,
    handleLeavePageWithoutSave,
    isNotEdited,
    hideTeamTooltip = false,
}) => {
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
    const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        setMenuAnchorEl(event.currentTarget)
    }
    const handleCloseMenu = () => {
        setMenuAnchorEl(null)
    }
    const [type, setType] = useState<TypeState>(undefined)
    const { uuid } = useParams<{ uuid: string }>()
    const [t] = useNewTranslation()
    const history = useHistory()
    const team = useSelector(selectTeam(uuid as string))
    const checkTeamPermission = useCheckTeamPermissionFunction()
    const isInternalUser = useSelector(selectIsInternalUser)
    const canPayCampaign = usePermission(CampaignStatusControl.PayCampaign)
    const handleSwitchViewType = (type: SwitchViewType) => {
        switch (type) {
            case SwitchViewType.MEMBERS:
                history.push(`/team/${uuid}/members`)
                break
            case SwitchViewType.ROLE_MANAGEMENT:
                history.push(`/teams/${uuid}/roles`)
                break
            case SwitchViewType.BRAND_ACCESS:
                history.push(`/team/${uuid}/brands`)
                break
            case SwitchViewType.BUSINESS_DETAILS:
                history.push(`/teams/${uuid}/business-details`)
                break
        }
    }

    const teamTooltipDisplay = useMemo(() => checkTeamPermission(team, true), [team]) && !hideTeamTooltip
    const onClose = () => setType(undefined)

    const tabOptions: TabOption[] = [
        {
            id: 'members',
            label: t('manageTeams.teamPage.membersTab'),
            onClick: () => {
                if (!isNotEdited) {
                    return handleLeavePageWithoutSave(SwitchViewType.MEMBERS)
                } else {
                    handleSwitchViewType(SwitchViewType.MEMBERS)
                }
            },
        },
        {
            id: 'roles',
            label: t('manageTeams.teamPage.rolesAndPermissionsTab'),
            onClick: () => {
                handleSwitchViewType(SwitchViewType.ROLE_MANAGEMENT)
            },
        },
    ]

    if (!team?.is_internal) {
        tabOptions.push({
            id: 'brands',
            label: t('manageTeams.teamPage.brandAccessTab'),
            onClick: () => {
                if (!isNotEdited) {
                    return handleLeavePageWithoutSave(SwitchViewType.BRAND_ACCESS)
                } else {
                    handleSwitchViewType(SwitchViewType.BRAND_ACCESS)
                }
            },
        })
    }

    if (!team?.is_internal && (isInternalUser || canPayCampaign)) {
        tabOptions.push({
            id: 'businessDetails',
            label: t('manageTeams.teamPage.businessInformationTab'),
            onClick: () => {
                handleSwitchViewType(SwitchViewType.BUSINESS_DETAILS)
            },
        })
    }

    const optionModal: ModalState = useMemo(() => {
        return { team: team, type: type }
    }, [team, type])

    return (
        <>
            <div className={className}>
                <div className="title-container">
                    <div className="title">{team && team.name}</div>
                    {teamTooltipDisplay && (
                        <div>
                            <IconButton
                                form="icon"
                                size="s"
                                color="neutral"
                                icon="dotsThreeOutline"
                                iconForm="fill"
                                onClick={handleOpenMenu}
                            />
                            <Menu
                                size="s"
                                open={Boolean(menuAnchorEl)}
                                anchorEl={menuAnchorEl}
                                onClose={handleCloseMenu}
                                width={160}
                            >
                                <MenuItem.Text
                                    text={t('button.editTeamName')}
                                    onClick={() => {
                                        handleCloseMenu()
                                        setType(MODAL_TYPE.EDIT_TEAM_NAME)
                                    }}
                                />
                                <MenuItem.Text
                                    text={t('button.deleteTeam')}
                                    onClick={() => {
                                        handleCloseMenu()
                                        setType(MODAL_TYPE.DELETE_TEAM)
                                    }}
                                    textColor="error"
                                />
                            </Menu>
                        </div>
                    )}
                </div>
                {showTab && (
                    <Tab
                        dataTestId="test-teams-tab"
                        tabItemAlign="left"
                        selectedTab={switchProps.currentViewType}
                        tabs={tabOptions}
                    />
                )}
            </div>
            <PredefinedModal onClose={onClose} {...optionModal} />
        </>
    )
}
export default styled(Header)`
    position: relative;
    z-index: 180;
    padding-bottom: ${spacing[400]};
    .title-container {
        display: flex;
        gap: 10px;
        align-items: center;
        @media screen and (max-width: 375px) {
            justify-content: space-between;
        }
    }
    .title {
        font-weight: ${theme.fontWeight.bold};
        font-size: ${theme.newFontSizes.l};
        color: ${theme.newColors.neutral700};
        max-width: 542px;
        @media screen and (max-width: 768px) {
            max-width: 236px;
        }
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    #header-tool-tip {
        height: 28px;
        width: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        cursor: pointer;
        position: relative;
        svg {
            background-color: transparent;
        }
        &:hover {
            svg {
                fill: ${theme.newColors.neutral700};
            }
            background-color: ${theme.newColors.neutral200};
        }
    }
`
